// Draws export frames: video cells with the WebGL renderer, then burn-ins on a 2D canvas (F-13.1, F-13.2, F-13.5).
// The client template uses its own drawing function that only receives whitelisted data (D-09).

import { GLRenderer, type CellParams } from '../render/gl-renderer';
import type { CellSpec, TemplateLayout } from './templates';
import type { Point, SourceId, SpatialTransform, ViewAdjustmentValues } from '../core/types';
import { drawLabel, drawPixelGrid } from '../app/view';
import { slotLabel } from '../core/types';

const BLACK: [number, number, number] = [0, 0, 0];
const ZOOM_OUT: [number, number, number] = [74 / 255, 74 / 255, 74 / 255];

export interface CompositorSources {
  frameA: { w: number; h: number };
  transforms: Partial<Record<SourceId, SpatialTransform>>;
  native: Partial<Record<SourceId, { w: number; h: number }>>;
}

export class Compositor {
  readonly glCanvas: OffscreenCanvas;
  readonly gl: GLRenderer;
  readonly canvas: OffscreenCanvas;
  readonly ctx: OffscreenCanvasRenderingContext2D;

  constructor(readonly layout: TemplateLayout) {
    this.glCanvas = new OffscreenCanvas(layout.width, layout.height);
    this.gl = new GLRenderer(this.glCanvas);
    this.canvas = new OffscreenCanvas(layout.width, layout.height);
    this.ctx = this.canvas.getContext('2d', { alpha: false })!;
  }

  /** Renders the video layer. Sources must already be uploaded to this.gl under their ids. */
  drawVideo(
    s: CompositorSources,
    opts: {
      crosshair: Point;
      zoom: number;
      nearest: boolean;
      diff: { gain: number; heat: boolean; luma: boolean; band?: 0 | 1 | 2 };
      wipe: { pos: number; angleRad: number };
      adjust: ViewAdjustmentValues | null;
    },
  ): void {
    const L = this.layout;
    for (const [id, n] of Object.entries(s.native)) if (n) this.gl.setNativeSize(id, n.w, n.h);
    this.gl.begin(L.width, L.height, BLACK);
    const fa = s.frameA;
    for (const c of L.cells) this.gl.drawCell(this.cellParams(c, s, opts, fa));
    this.ctx.drawImage(this.glCanvas, 0, 0);
  }

  private cellParams(c: CellSpec, s: CompositorSources, opts: Parameters<Compositor['drawVideo']>[1], fa: { w: number; h: number }): CellParams {
    const xf = (id: SourceId) => {
      const t = id === 'A' ? undefined : s.transforms[id];
      return t ? { dx: t.dx, dy: t.dy, sx: t.sx, sy: t.sy } : { dx: 0, dy: 0, sx: 1, sy: 1 };
    };
    // Full cells show the whole A frame; zoom cells show the crosshair area at the zoom level.
    let viewScale = fa.w / c.w; // A px per output px
    let origin = { x: 0, y: 0 };
    if (c.kind === 'zoom') {
      viewScale = fa.w / c.w / opts.zoom;
      origin = { x: opts.crosshair.x - (c.w * viewScale) / 2, y: opts.crosshair.y - (c.h * viewScale) / 2 };
    }
    const base: CellParams = {
      rect: { x: c.x, y: c.y, w: c.w, h: c.h },
      mode: 'single',
      a: c.src,
      b: null,
      xfA: xf(c.src),
      xfB: { dx: 0, dy: 0, sx: 1, sy: 1 },
      frameA: fa,
      viewOrigin: origin,
      viewScale,
      nearest: c.kind === 'zoom' ? opts.nearest : false,
      outside: c.kind === 'zoom' ? ZOOM_OUT : BLACK,
      wipe: opts.wipe,
      opacity: 0.5,
      diff: opts.diff,
      channel: 0,
      texture: false,
      adjA: opts.adjust,
      adjB: null,
    };
    if (c.kind === 'diff' && c.b) return { ...base, mode: 'difference', a: c.src, b: c.b, xfA: xf(c.src), xfB: xf(c.b), adjB: opts.adjust };
    if (c.kind === 'wipe' && c.b) return { ...base, mode: 'wipe', a: c.src, b: c.b, xfA: xf(c.src), xfB: xf(c.b), adjB: opts.adjust };
    return base;
  }

  /** Maps A coordinates into a cell (for burn-ins). */
  mapper(c: CellSpec, fa: { w: number; h: number }, crosshair: Point, zoom: number): (p: Point) => Point {
    if (c.kind === 'zoom') {
      const s = (c.w / fa.w) * zoom;
      return (p) => ({ x: c.x + c.w / 2 + (p.x - crosshair.x) * s, y: c.y + c.h / 2 + (p.y - crosshair.y) * s });
    }
    const s = c.w / fa.w;
    return (p) => ({ x: c.x + p.x * s, y: c.y + p.y * s });
  }
}

// ------------------------------------------------------------------------------------------ internal burn-ins

export interface InternalBurnIn {
  labels: Partial<Record<SourceId, string>>;
  alignInfo: Partial<Record<SourceId, string>>;
  timecode: string | null;
  frameLabel: string | null;
  crosshair: Point | null;
  /** Centre of the zoom cells (the crosshair position, also when the crosshair itself is not burned in). */
  center: Point;
  zoom: number;
  /** Pixel-comparison grid on the zoom cells (F-06.10); labels at the intersections from 2000 %. */
  grid: { labels: boolean } | null;
  notes: string[];
  frameA: { w: number; h: number };
}

export function drawInternalBurnIn(comp: Compositor, b: InternalBurnIn): void {
  const ctx = comp.ctx;
  const L = comp.layout;
  const k = Math.max(1, L.height / 1080);
  for (const c of L.cells) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(c.x, c.y, c.w, c.h);
    ctx.clip();
    const map = comp.mapper(c, b.frameA, b.center, b.zoom);
    if (b.grid && c.kind === 'zoom') {
      const o = map({ x: 0, y: 0 });
      drawPixelGrid(ctx, c, o, map({ x: 1, y: 0 }).x - o.x, k, b.grid.labels);
    }
    if (b.crosshair && c.kind !== 'zoom') {
      const p = map(b.crosshair);
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 3 * k;
      const arm = 22 * k;
      const gap = 6 * k;
      const seg = () => {
        ctx.beginPath();
        ctx.moveTo(p.x - gap - arm, p.y);
        ctx.lineTo(p.x - gap, p.y);
        ctx.moveTo(p.x + gap, p.y);
        ctx.lineTo(p.x + gap + arm, p.y);
        ctx.moveTo(p.x, p.y - gap - arm);
        ctx.lineTo(p.x, p.y - gap);
        ctx.moveTo(p.x, p.y + gap);
        ctx.lineTo(p.x, p.y + gap + arm);
        ctx.stroke();
      };
      seg();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = k;
      seg();
    }
    // Label block (top-left of each cell).
    const title = c.kind === 'diff' ? `|${slotLabel(c.src)} − ${slotLabel(c.b ?? 'B1')}|` : c.kind === 'wipe' ? `${slotLabel(c.src)} ｜ ${slotLabel(c.b ?? 'B1')}` : (b.labels[c.src] ?? slotLabel(c.src));
    // Zoom cells repeat the label above with "_zoom" (e.g. "A_zoom 400%").
    const lines = [title + (c.kind === 'zoom' ? '_zoom' : '') + (c.kind === 'zoom' ? `  ${Math.round(b.zoom * 100)}%` : '')];
    const al = c.kind === 'diff' ? b.alignInfo[c.b!] : b.alignInfo[c.src];
    if (al && c.kind !== 'zoom') lines.push(al);
    drawLabel(ctx, lines.join('\n'), c.x + 12 * k, c.y + 12 * k, '#bbbbbb', true, k);
    ctx.restore();
  }
  const bottom: string[] = [];
  if (b.timecode) bottom.push(b.timecode);
  if (b.frameLabel) bottom.push(b.frameLabel);
  bottom.push(...b.notes);
  if (bottom.length) drawLabel(ctx, bottom.join('   '), 12 * k, L.height - 12 * k, '#bbbbbb', false, k);
}

// ------------------------------------------------------------------------------------------ client burn-ins (D-09)

/** Only whitelisted data may appear: the labels chosen for the client. */
export interface ClientBurnIn {
  labels: { before: string; after: string };
  /** Which cells have a picture (empty quad cells get no label). */
  present: SourceId[];
}

export function drawClientBurnIn(comp: Compositor, b: ClientBurnIn): void {
  const ctx = comp.ctx;
  const L = comp.layout;
  const k = Math.max(1, L.height / 1080);
  const font = `600 ${Math.round(22 * k)}px system-ui, "Microsoft JhengHei", "PingFang TC", sans-serif`;
  const tag = (text: string, x: number, y: number, align: 'left' | 'right') => {
    ctx.font = font;
    const w = ctx.measureText(text).width + 24 * k;
    const h = 36 * k;
    const x0 = align === 'left' ? x : x - w;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(x0, y, w, h);
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x0 + 12 * k, y + h / 2);
    ctx.textBaseline = 'alphabetic';
  };
  // One number per version (the zoom cells of side-by-side + zoom repeat their version's label with "_zoom").
  const bCells = L.cells.filter((c) => c.src !== 'A' && c.kind !== 'zoom' && b.present.includes(c.src));
  for (const c of L.cells) {
    if (c.kind === 'wipe') {
      tag(b.labels.before, c.x + 16 * k, c.y + 16 * k, 'left');
      tag(b.labels.after, c.x + c.w - 16 * k, c.y + 16 * k, 'right');
    } else if (b.present.includes(c.src)) {
      // Several versions: After 1, After 2, … (never the internal source names).
      const n = bCells.findIndex((x) => x.src === c.src);
      const label = c.src === 'A' ? b.labels.before : bCells.length > 1 ? `${b.labels.after} ${n + 1}` : b.labels.after;
      tag(c.kind === 'zoom' ? `${label}_zoom` : label, c.x + 16 * k, c.y + 16 * k, 'left');
    }
  }
}

/** Text title / end card of an export (title and subtitle). */
export function drawSlate(comp: Compositor, title: string, subtitle: string): void {
  const ctx = comp.ctx;
  const L = comp.layout;
  const k = Math.max(1, L.height / 1080);
  ctx.fillStyle = '#101010';
  ctx.fillRect(0, 0, L.width, L.height);
  ctx.fillStyle = '#f2f2f2';
  ctx.textAlign = 'center';
  ctx.font = `600 ${Math.round(56 * k)}px system-ui, "Microsoft JhengHei", "PingFang TC", sans-serif`;
  ctx.fillText(title, L.width / 2, L.height / 2);
  ctx.font = `400 ${Math.round(28 * k)}px system-ui, "Microsoft JhengHei", "PingFang TC", sans-serif`;
  ctx.fillStyle = '#9a9a9a';
  ctx.fillText(subtitle, L.width / 2, L.height / 2 + 60 * k);
  ctx.textAlign = 'start';
}

/** One frame of an opening video, fitted into the output (letterboxed on black). */
export function drawIntroFrame(comp: Compositor, sample: { displayWidth: number; displayHeight: number; draw(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, w: number, h: number): void } | null): void {
  const ctx = comp.ctx;
  const L = comp.layout;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, L.width, L.height);
  if (!sample) return;
  const k = Math.min(L.width / sample.displayWidth, L.height / sample.displayHeight);
  const w = sample.displayWidth * k;
  const h = sample.displayHeight * k;
  sample.draw(ctx, (L.width - w) / 2, (L.height - h) / 2, w, h);
}
